<template>
  <div class="sms-composer">
    <div class="compose__setup">
      <div class="compose__tool">
        <span>Link a campaign</span>
        <campaign-picker
          :url="campaignsUrl"
          v-model="campaignId"
          name="campaign_id"
          placeholder="None"
          label="Link a campaign to this text"
        />
      </div>
    </div>

    <div class="compose__fields">
      <p class="compose__row">
        <span class="compose__key">To</span>
        <span class="compose__value">{{ audience }}</span>
      </p>
      <p class="compose__row">
        <span class="compose__key">From</span>
        <span class="compose__value">{{ frame.brand }}</span>
      </p>

      <!--
        A plain textarea: a text has no formatting to offer, and a rich editor
        would suggest there was some. maxlength mirrors EmailCampaignRequest::SMS_MAX.
      -->
      <label class="visually-hidden" for="sms_body">Text message</label>
      <textarea id="sms_body" ref="body" class="sms-composer__body" name="sms_body" rows="5"
                required :maxlength="maxLength" :data-error="messages.smsBody"
                placeholder="Write your text to the selected creators&hellip;" v-model="body"></textarea>
    </div>

    <div class="compose__tags">
      <button v-for="placeholder in placeholders" :key="placeholder" type="button"
              class="compose__tag" @click="insertPlaceholder(placeholder)">
        &#123;&#123; {{ placeholder }} &#125;&#125;
      </button>
    </div>

    <!--
      The number that matters is segments, not characters: it is what each
      creator costs. Said per creator because the audience multiplies it.
    -->
    <p class="sms-composer__meter" :class="{ 'is-over': segments > 1 }">
      {{ segments }} {{ segments === 1 ? 'segment' : 'segments' }} per creator
      <span>&middot; {{ length }} characters with everything added{{ unicode ? ', non-GSM characters (70 per segment)' : '' }}</span>
    </p>

    <p class="marketing-hint">
      Every text starts with &ldquo;{{ frame.brand }}:&rdquo; and ends with &ldquo;{{ frame.optOut }}&rdquo;<template
        v-if="campaignId">, with the challenge link just before it</template>. Only creators who verified their
      phone and agreed to texts receive it, at most one marketing text a day, and never at night &mdash; those
      get it in the morning. Messages are sent according to each creator&rsquo;s timezone. The platform timezone is
      used when unavailable.
    </p>

    <div class="sms-composer__preview">
      <div class="section-title">
        <h1>Preview</h1>
        <p>What a creator&rsquo;s phone shows, updating as you type.</p>
      </div>
      <div class="sms-phone">
        <p class="sms-phone__sender">{{ frame.brand }}</p>
        <p class="sms-phone__bubble">{{ preview }}</p>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The text step of the campaign builder: the copy, its cost, and what arrives.
 *
 * Unlike the email preview this one is rendered here and not on the server. A
 * text has no layout to get wrong - it is the client's words with the same
 * three pieces around them every time - and the one thing worth seeing live is
 * the segment count, which a round trip per keystroke would lag behind.
 *
 * The pieces themselves come from the server (smsFrame, tokens) and the
 * counting rule is TwilioClient::segments(), so the numbers here are the ones
 * the send will produce.
 */
import CampaignPicker from './CampaignPicker.vue'

export default {
  name: 'SmsComposer',

  components: { CampaignPicker },

  props: {
    messages: {
      type: Object,
      required: true
    },
    campaignsUrl: {
      type: String,
      required: true
    },
    placeholders: {
      type: Array,
      required: false,
      default: () => []
    },
    audience: {
      type: String,
      required: false,
      default: ''
    },
    // { brand, optOut, link } - what SmsChannel and ClientSmsBlastMessage put
    // around the client's words, the link at the length a real one has.
    frame: {
      type: Object,
      required: true
    },
    tokens: {
      type: Object,
      required: false,
      default: () => ({})
    },
    maxLength: {
      type: Number,
      required: true
    },
    initial: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  data() {
    return {
      body: this.initial.smsBody || '',
      campaignId: this.initial.campaignId ? String(this.initial.campaignId) : ''
    }
  },

  computed: {
    preview() {
      const words = this.body.trim().replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, key) => {
        const value = this.tokens[key.toLowerCase()]

        return value === undefined ? match : String(value)
      })

      let text = this.frame.brand ? `${this.frame.brand}: ${words}` : words

      if (this.campaignId && this.frame.link) {
        text += ` ${this.frame.link}`
      }

      // Same exact-match rule as SmsChannel::withOptOutNotice().
      if (this.frame.optOut && !text.includes(this.frame.optOut)) {
        text += ` ${this.frame.optOut}`
      }

      return text
    },

    // Code points, like mb_strlen() on the server.
    length() {
      return [...this.preview].length
    },

    unicode() {
      return /[^\x00-\x7F]/.test(this.preview)
    },

    // TwilioClient::segments(), line for line.
    segments() {
      const single = this.unicode ? 70 : 160
      const multi = this.unicode ? 67 : 153

      return this.length <= single ? 1 : Math.ceil(this.length / multi)
    }
  },

  methods: {
    insertPlaceholder(placeholder) {
      const field = this.$refs.body
      const tag = `{{ ${placeholder} }}`
      const start = field.selectionStart ?? this.body.length
      const end = field.selectionEnd ?? this.body.length

      this.body = this.body.slice(0, start) + tag + this.body.slice(end)

      this.$nextTick(() => {
        field.focus()
        field.setSelectionRange(start + tag.length, start + tag.length)
      })
    }
  }
}
</script>
